<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Terminology';
    let nextLink= '/Tutorial/Creating_your_Metamodel';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="welcome-to-the-freon-tutorial-1"
  bind:intersecting={visibleById["welcome-to-the-freon-tutorial-1"]}
  onIntersectChange={(visible) => setVisible("welcome-to-the-freon-tutorial-1", visible)}
>Welcome to the Freon Tutorial!</SectionComponent>
<p>This step-by-step guide will teach you everything you need to know to build your own domain-specific language (DSL) using <strong>Freon</strong>.<br>
You will learn how to create the supporting tools—such as the editor, validator, and interpreter—that enable your end-users to work efficiently with your DSL.</p>
<p>You can also consult the full documentation, or — if you’re eager to get started locally — <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
>create a project</a>.</p>
<p>We assume that you have an interest in DSLs and are familiar with terms such as <em>metamodel</em>, <em>abstract syntax</em>, and <em>model</em>, as well as abbreviations like <em>AST</em>.<br>
If not, please refer to introductory materials, such as the
book <a href="https://www.manning.com/books/building-user-friendly-dsls" target="_blank"><em>Building User-Friendly DSLs</em></a> by Meinte Boersma.
If you just need a refresher, see the <a
  href="/Documentation/Terminology"
>Freon Terminology</a> page. Some basic knowledge of UML is useful, but not strictly required.</p>
<SectionComponent tag="h2" 
  id="the-dsl-computer-aided-learning-2"
  bind:intersecting={visibleById["the-dsl-computer-aided-learning-2"]}
  onIntersectChange={(visible) => setVisible("the-dsl-computer-aided-learning-2", visible)}
>The DSL: Computer Aided Learning</SectionComponent>
<p>To illustrate how Freon can be used, this tutorial focuses on a domain called <strong>Computer Aided Learning (CAL)</strong>.</p>
<p>Our hypothetical client is a company that offers educational services to children of various ages.<br>
Through multiple websites, it helps students learn subjects such as arithmetic, mathematics, geography, history, biology, and road safety.</p>
<h3 id="the-clients-needs-1">The Client’s Needs</h3>
<p>The company wants to create and manage educational webpages for these subjects in a way that is both <strong>lean</strong> and <strong>flexible</strong>.<br>
They need a system to define:</p>
<ul>
<li>The <strong>content</strong> of each page, and</li>
<li>The <strong>flow</strong> between pages, depending on how each student performs.</li>
</ul>
<p>For example:</p>
<ul>
<li>When a pupil makes many mistakes, the next page should provide extra explanations or more practice material.</li>
<li>When a pupil progresses easily, the next page should offer more advanced exercises to maintain engagement.</li>
</ul>
<p>In addition, the company wants to <strong>test and validate</strong> the page flow before generating the actual webpages.</p>
<h3 id="how-the-dsl-addresses-these-needs-2">How the DSL Addresses These Needs</h3>
<p>Our task is to design a <strong>domain-specific language</strong> that allows the company to define these pages and their flow in a simple, structured, and testable way.<br>
With the DSL, non-programmers can specify educational content and behavior directly, while developers can generate the necessary website logic automatically.</p>
<p>As you can see, satisfying this client’s requirements gives us an excellent example of how a well-designed DSL can combine <strong>expressiveness</strong>, <strong>usability</strong>, and <strong>automation</strong>.</p>
<SectionComponent tag="h2" 
  id="how-to-use-this-tutorial-3"
  bind:intersecting={visibleById["how-to-use-this-tutorial-3"]}
  onIntersectChange={(visible) => setVisible("how-to-use-this-tutorial-3", visible)}
>How to Use This Tutorial</SectionComponent>
<p>This tutorial consists of <strong>nine lessons</strong>, each covering a specific topic while building upon the previous ones.<br>
For the best learning experience, follow the lessons in the given order.</p>
<p>The easiest way to follow along is to use the <em>create project</em> command and choose the language <code>Education</code>.<br>
This lets you recreate the example project and experiment with its components.</p>
<pre class="language-bash"><code class="language-bash">npm create freon@latest</code></pre>
<p>In the <code>Education</code> project, you’ll find all definition files for each lesson in separate folders.<br>
This prevents you from being distracted by details not yet introduced.<br>
For example, the files for Lesson 3 are in the folder <code>lesson3-defs</code>, and so on.<br>
Lesson 1 files are located in the <code>src/defs</code> folder (see <a
  href="/Tutorial/Overview#where-do-the-files-go-4"
>Where do the files go?</a>).</p>
<p>Before starting a new lesson, empty the <code>src/defs</code> folder and copy in the files for the lesson you’re about to follow.<br>
You’ll also find example models for the lessons in the <strong>modelstore</strong> folder.<br>
(For details on the project contents, see <a
  href="/Documentation/Overview/Getting_Started#project-structure-5"
>Project Structure</a>.)<br>
Use the model <code>Lesson1</code> for Lessons 1–3, <code>Lesson4</code> for Lessons 4–5, and <code>Lesson6</code> for the remaining lessons.</p>
<p>For tips on using the editor, click the <strong>question mark</strong> icon on the editor’s top bar.</p>
<SectionComponent tag="h2" 
  id="where-do-the-files-go-4"
  bind:intersecting={visibleById["where-do-the-files-go-4"]}
  onIntersectChange={(visible) => setVisible("where-do-the-files-go-4", visible)}
>Where Do the Files Go?</SectionComponent>
<p>Your project is set up so that all Freon definition files reside in the <code>src/defs</code> folder.<br>
If you prefer a different structure, you can modify your <code>package.json</code> file.<br>
Check the scripts for <code>generate</code> and <code>clean-gen</code>, which reference <code>src/defs</code>.<br>
Changing these paths allows you to place your language definition files anywhere you like.
You might also adjust the output location (currently <code>src/freon</code>).</p>
<pre class=""><code class="">"generate": "freon -v all -d src/defs -o src/freon",
"clean-gen": "freon clean-it -d src/defs -o src/freon",</code></pre>
<p>In this tutorial, we’ll create several Freon definition files.<br>
Note that the <strong>file extension</strong> (e.g., <code>.ast</code>, <code>.valid</code>) is important, while the file name is not.<br>
All metamodel definitions belong in <code>.ast</code> files, editor definitions in <code>.edit</code> files, and so on.</p>
<p>If you’re following this tutorial in your own IDE and notice that the browser occasionally displays an empty page after regeneration, don’t worry.<br>
This happens because the browser reloads faster than the regeneration process completes.<br>
In the browser’s developer console, you might see the message:
<code>Uncaught SyntaxError: Unexpected end of input</code>.
Simply reload the page—everything will display correctly afterward.</p>


<PrevNextSection {prevLink} {nextLink} />